/**
 * Step 1 · Identity & Location
 * Basic project identity + location fields. These drive AI suggestions
 * across every subsequent step, so we require Name + Country here.
 */
import { useQuery } from "@tanstack/react-query";
import { IdentificationCard, MapPin, User, Buildings, Sparkle, Info } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import SearchableSelect from "../parts/SearchableSelect";

const STAGES = [
  { id: "early_exploration",    label: "Early Exploration" },
  { id: "advanced_exploration", label: "Advanced Exploration" },
  { id: "mre",                    label: "Mineral Resource Estimation" },
  { id: "reserve",                label: "Mineral Reserve Estimation" },
  { id: "mine_planning",         label: "Mine Planning" },
  { id: "feasibility",            label: "Feasibility Study" },
  { id: "cpr_update",            label: "CPR / Technical Report Update" },
];

export default function Step1Identity({ form, set }) {
  const countriesQ = useQuery({
    queryKey: ["master-countries"],
    queryFn:  () => api.get("/master/countries").then((r) => r.data.results),
    staleTime: 5 * 60_000,
  });

  return (
    <section className="space-y-4" data-testid="step-1-identity">
      <StepHeader step="Step 1" title="Identity & Location"
        subtitle="Name the project and locate it. AI will suggest defaults across every subsequent step based on your country and coordinates." />

      <div className="rounded-lg bg-white border border-slate-200 divide-y divide-slate-100">
        <FieldRow icon={IdentificationCard} label="Project Name" required
                     hint="A concise operational name — e.g. 'Nyanzaga Gold' or 'Balama Graphite'.">
          <input type="text" value={form.name || ""}
                   onChange={(e) => set({ name: e.target.value })}
                   placeholder="e.g. Nyanzaga Gold"
                   data-testid="input-project-name"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>

        <FieldRow icon={Buildings} label="Client / Owner" hint="The company that owns / commissioned the study.">
          <input type="text" value={form.client || ""}
                   onChange={(e) => set({ client: e.target.value })}
                   placeholder="e.g. Acme Resources Ltd."
                   data-testid="input-client"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>

        <FieldRow icon={Buildings} label="Consultant" hint="External consultant preparing the report (optional).">
          <input type="text" value={form.consultant || ""}
                   onChange={(e) => set({ consultant: e.target.value })}
                   placeholder="e.g. Shanvi Resources"
                   data-testid="input-consultant"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>

        <FieldRow icon={User} label="Project Manager" hint="Day-to-day owner of the study (optional).">
          <input type="text" value={form.project_manager || ""}
                   onChange={(e) => set({ project_manager: e.target.value })}
                   placeholder="e.g. Jane Smith"
                   data-testid="input-project-manager"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>

        <FieldRow icon={MapPin} label="Country" required
                     hint="Drives CRS defaults, currency, timezone and reporting standard.">
          <SearchableSelect
            testId="select-country"
            value={form.country}
            options={(countriesQ.data || []).map((c) => ({ value: c.iso2, label: c.name, hint: c.iso3, meta: c.currency_code }))}
            onChange={(v) => set({ country: v })}
            placeholder="Search by name or ISO code…"
          />
        </FieldRow>

        <FieldRow icon={MapPin} label="State / Province">
          <input type="text" value={form.state || ""}
                   onChange={(e) => set({ state: e.target.value })}
                   placeholder="e.g. Geita Region"
                   data-testid="input-state"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>

        <FieldRow icon={MapPin} label="District / County">
          <input type="text" value={form.district || ""}
                   onChange={(e) => set({ district: e.target.value })}
                   placeholder="e.g. Chunya District"
                   data-testid="input-district"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>

        <FieldRow icon={MapPin} label="Village / Locality">
          <input type="text" value={form.village || ""}
                   onChange={(e) => set({ village: e.target.value })}
                   placeholder="e.g. Kapalala"
                   data-testid="input-village"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>

        <FieldRow icon={MapPin} label="Latitude / Longitude"
                     hint="Approximate centre — AI uses this to auto-detect UTM zone.">
          <div className="grid grid-cols-2 gap-2">
            <input type="number" step="any" value={form.latitude ?? ""}
                     onChange={(e) => set({ latitude: e.target.value === "" ? undefined : Number(e.target.value) })}
                     placeholder="Latitude (-90…90)"
                     data-testid="input-latitude"
                     className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
            <input type="number" step="any" value={form.longitude ?? ""}
                     onChange={(e) => set({ longitude: e.target.value === "" ? undefined : Number(e.target.value) })}
                     placeholder="Longitude (-180…180)"
                     data-testid="input-longitude"
                     className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
          </div>
        </FieldRow>

        <FieldRow icon={Sparkle} label="Project Stage" hint="Determines which pipeline modules are active by default.">
          <select value={form.project_stage || "early_exploration"}
                    onChange={(e) => set({ project_stage: e.target.value })}
                    data-testid="select-project-stage"
                    className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]">
            {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </FieldRow>
      </div>
    </section>
  );
}

export function StepHeader({ step, title, subtitle }) {
  return (
    <header>
      <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">{step}</div>
      <h2 className="font-heading font-black text-2xl text-slate-900 tracking-tight">{title}</h2>
      {subtitle && <p className="text-[13px] text-slate-600 mt-1">{subtitle}</p>}
    </header>
  );
}

export function FieldRow({ icon: Icon, label, required, hint, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-3 md:gap-6 px-4 py-3 items-start">
      <div className="flex items-start gap-2">
        {Icon && (
          <div className="w-8 h-8 shrink-0 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
            <Icon size={13} className="text-slate-500" weight="duotone" />
          </div>
        )}
        <div>
          <div className="text-[12.5px] font-semibold text-slate-900">
            {label}{required && <span className="ml-1 text-rose-600">*</span>}
          </div>
          {hint && <div className="text-[10.5px] text-slate-500 mt-0.5">{hint}</div>}
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}
